import { faImage } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils';
import { cva } from 'class-variance-authority';
import { Icon } from '@/modules/Icon';
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { PictureProps } from '@/shared/types/picture';
import type { IconFontSize } from '@/shared/types/types';

function pictureSizeToIconSize(size: PictureProps['size']): IconFontSize {
  if (!size || size === 'auto') return 'md';
  return size;
}

/**
 * Inactivo alineado a Figma **Cards** [233:11229](https://www.figma.com/design/3w0SWvV7C5tDKUybsOcgA4/Cards?node-id=233-11229):
 * capa `bg-fill-tertiary` (#f0f1f2) como en Figma; contenedor con `mix-blend-luminosity`; imagen con brillo/contraste suaves.
 *
 * Si un consumidor fuerza `img { filter: none !important }` o anula `mix-blend-mode`, el resultado puede no coincidir.
 *
 * Sin `src` válido o tras error de carga de imagen se muestra el icono Font Awesome **Image** (regular) en superficie terciaria, en lugar de un SVG raster externo.
 */
const PICTURE_DISABLED_IMG_STYLE: CSSProperties = {
  WebkitFilter: 'brightness(1.06) contrast(0.88)',
  filter: 'brightness(1.06) contrast(0.88)',
};

/**
 * Imagen con variantes de tamaño, radio y estado *disabled* alineado a Figma.
 * Los `<img>` usan **`draggable={false}`** para no iniciar arrastre nativo hacia el escritorio u otras apps.
 */
export const Picture = ({
  src,
  alt,
  className,
  loading = 'lazy',
  square = false,
  size = 'auto',
  radius = 'none',
  shadow = false,
  border = false,
  fillParent = false,
  disabled = false,
}: PictureProps) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (process.env.NODE_ENV !== 'production') {
    if (fillParent && size !== 'auto') {
      // eslint-disable-next-line no-console
      console.warn(
        'Picture: con fillParent el tamaño lo define el contenedor; usa size="auto" o omite size.'
      );
    }
    if (fillParent && square) {
      // eslint-disable-next-line no-console
      console.warn(
        'Picture: fillParent ignora square; la proporción la marca el contenedor padre.'
      );
    }
  }

  const ornament = cva('', {
    variants: {
      radius: {
        none: 'ui-rounded-none',
        xs: 'ui-rounded-control-xs',
        sm: 'ui-rounded-image-sm',
        md: 'ui-rounded-image-md',
        lg: 'ui-rounded-control-lg',
        pill: 'ui-rounded-image-pill',
      },
      shadow: {
        true: 'ui-shadow-03',
        false: '',
      },
      border: {
        true: 'ui-border-1 ui-border-default',
        false: '',
      },
    },
  });

  const variants = cva('ui-relative ui-object-cover', {
    variants: {
      square: {
        true: 'ui-aspect-square',
        false: 'ui-aspect-video',
      },
      size: {
        auto: 'ui-h-auto ui-w-full',
        xs: `ui-h-6 ${square ? 'ui-w-6' : ''}`,
        sm: `ui-h-10 ${square ? 'ui-w-10' : ''}`,
        md: `ui-h-16 ${square ? 'ui-w-16' : ''}`,
        lg: `ui-h-[128px] ${square ? 'ui-w-[128px]' : ''}`,
        xl: `ui-h-[240px] ${square ? 'ui-w-[240px]' : ''}`,
      },
      radius: {
        none: 'ui-rounded-none',
        xs: 'ui-rounded-control-xs',
        sm: 'ui-rounded-image-sm',
        md: 'ui-rounded-image-md',
        lg: 'ui-rounded-control-lg',
        pill: 'ui-rounded-image-pill',
      },
      shadow: {
        true: 'ui-shadow-03',
        false: '',
      },
      border: {
        true: 'ui-border-1 ui-border-default',
        false: '',
      },
    },
  });

  const trimmedSrc = src.trim();
  const showRaster = trimmedSrc.length > 0 && !hasError;

  const handleImageError = useCallback(() => {
    setHasError(true);
  }, []);

  const imgClassName = cn(
    fillParent
      ? cn(
          'ui-h-full ui-w-full ui-min-h-full ui-min-w-full ui-object-cover ui-object-center',
          ornament({ radius, shadow, border })
        )
      : variants({ square, size, radius, shadow, border })
  );

  const useWebpSource =
    showRaster &&
    (/\.webp(\?|#|$)/i.test(src) || /[?&]fm=webp\b/i.test(src) || /format=webp/i.test(src));

  const iconSize = pictureSizeToIconSize(size);

  const rasterImgClass = cn(
    imgClassName,
    'ui-z-[1]',
    fillParent ? 'ui-absolute ui-inset-0' : 'ui-relative'
  );

  const fallbackClassName = cn(
    rasterImgClass,
    'ui-flex ui-items-center ui-justify-center ui-bg-fill-tertiary ui-text-tertiary'
  );

  const fallbackClassNameDisabled = cn(
    rasterImgClass,
    'ui-flex ui-items-center ui-justify-center ui-text-tertiary'
  );

  const pictureFallback = (
    <div role="img" aria-label={alt} className={fallbackClassName}>
      <Icon name={faImage} size={iconSize} color="tertiary" />
    </div>
  );

  const rootPictureClassName = cn(
    'ui-imj',
    fillParent
      ? 'ui-absolute ui-inset-0 ui-block ui-h-full ui-min-h-0 ui-w-full'
      : 'ui-relative ui-min-h-fit ui-min-w-fit',
    !fillParent && size === 'auto' ? 'ui-w-full' : '',
    className
  );

  const innerPictureClassName = cn(
    'ui-imj',
    fillParent
      ? 'ui-absolute ui-inset-0 ui-block ui-h-full ui-min-h-0 ui-w-full'
      : 'ui-relative ui-min-h-fit ui-min-w-fit',
    !fillParent && size === 'auto' ? 'ui-w-full' : ''
  );

  /** Misma pila que Figma Picture: fill tertiary + sombra en la capa base; `luminosity` en el raster. */
  const disabledStack = (
    <div className={cn(innerPictureClassName, 'ui-isolate')}>
      <div
        className={cn(
          'ui-pointer-events-none ui-absolute ui-inset-0 ui-z-0 ui-bg-fill-tertiary',
          shadow && 'ui-shadow-03'
        )}
        aria-hidden
      />
      {showRaster ? (
        <img
          src={src}
          alt={alt}
          className={rasterImgClass}
          style={PICTURE_DISABLED_IMG_STYLE}
          loading={loading}
          draggable={false}
          onError={handleImageError}
          data-picture-disabled=""
        />
      ) : (
        <div role="img" aria-label={alt} className={fallbackClassNameDisabled}>
          <Icon name={faImage} size={iconSize} color="tertiary" />
        </div>
      )}
    </div>
  );

  if (!disabled) {
    if (!showRaster) {
      return <div className={rootPictureClassName}>{pictureFallback}</div>;
    }

    return (
      <picture className={rootPictureClassName}>
        {useWebpSource ? <source srcSet={src} type="image/webp" /> : null}
        <img
          src={src}
          alt={alt}
          className={rasterImgClass}
          loading={loading}
          draggable={false}
          onError={handleImageError}
        />
      </picture>
    );
  }

  return <div className={cn(rootPictureClassName, 'ui-mix-blend-luminosity')}>{disabledStack}</div>;
};
